import { Injectable } from '@angular/core';
import { BaseBackendAppService } from '../../../common/services/base-backend-service/base-backend.service';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { lastValueFrom } from 'rxjs';
import ObjectUtil from '../../../common/util/object-util';
import { AppInstance } from '../../interfaces/app-instance';
import { AppDataState } from '../../../common/state-stores/app-data-state.state';
import { RequestUtil } from '../../../common/util/request-util';
import { ServiceErrorUtil } from '../../../common/util/service-error.util';

@Injectable({
  providedIn: 'root',
})
export class AppInstanceService {
  private static readonly APP_INSTANCES_TIME_STORAGE_KEY = 'appInstancesCacheTime';
  private static readonly APP_INSTANCES_DATA_STORAGE_KEY = 'appInstancesCacheData';
  private static readonly PLATFORM_PERMISSIONS_TIME_STORAGE_KEY = 'platformPermissionsCacheTime';
  private static readonly PLATFORM_PERMISSIONS_DATA_STORAGE_KEY = 'platformPermissionsCacheData';

  private readonly apollo: ApolloBase;
  // Seeded from sessionStorage so these caches survive a page reload, not just in-memory navigation.
  private platformPermissionsCacheTime: number | null = this.readCacheTime(AppInstanceService.PLATFORM_PERMISSIONS_TIME_STORAGE_KEY);
  private readonly appInstanceByIdCacheTime = new Map<number, number>();
  private appInstancesCacheTime: number | null = this.readCacheTime(AppInstanceService.APP_INSTANCES_TIME_STORAGE_KEY);
  private readonly PLATFORM_PERMISSIONS_CACHE_TTL_MS = 3 * 60 * 1000;
  // De-dupe concurrent callers hitting a cold/expired cache so they share one network call instead of each firing their own.
  private inflightAppInstances: Promise<AppInstance[]> | null = null;
  private inflightPlatformPermissions: Promise<any[]> | null = null;

  constructor(apollo: Apollo, private readonly appState: AppDataState) {
    this.apollo = apollo.use('platform');
  }

  private readCacheTime(key: string): number | null {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  private readCacheData<T>(key: string): T | null {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private writeCache(timeKey: string, dataKey: string, time: number, data: unknown): void {
    sessionStorage.setItem(timeKey, time.toString());
    sessionStorage.setItem(dataKey, JSON.stringify(data));
  }
  async getAppInstances(): Promise<AppInstance[]> {
    const cacheExpired =
      this.appInstancesCacheTime === null ||
      Date.now() - this.appInstancesCacheTime > this.PLATFORM_PERMISSIONS_CACHE_TTL_MS;

    if (!cacheExpired) {
      const cachedData = this.readCacheData<AppInstance[]>(AppInstanceService.APP_INSTANCES_DATA_STORAGE_KEY);
      if (cachedData) {
        return cachedData;
      }
    }

    if (!this.inflightAppInstances) {
      this.inflightAppInstances = this.fetchAppInstancesFromNetwork();
    }
    try {
      return await this.inflightAppInstances;
    } finally {
      this.inflightAppInstances = null;
    }
  }

  private async fetchAppInstancesFromNetwork(): Promise<AppInstance[]> {
    const requestId = RequestUtil.generateRequestId();
    const now = Date.now();
    let queryResult = await lastValueFrom(
        this.apollo.query<any>({
          fetchPolicy: 'network-only',
          context: {
            headers: {
              'App-Id': this.appState.getAppInstanceId() ?? 0,
              'Operator-Id': this.appState.getOperatorId() ?? 0,
              'Request-Id': requestId,
              'PermissionSet-Id': this.appState.getPermissionSetId() ?? 0,
            },
          },
          query: gql`
            query {
              appInstances {
                id
                operatorId
                operator {
                  name
                }
                modules {
                  name
                  permissions {
                    id
                    name
                    permissionType
                  }
                }
                appId
                app {
                  name
                  permissions {
                    id
                    name
                    permissionType
                  }
                }
              }
            }
          `,
        })
      );

    if (!queryResult.data) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error getting app instances', requestId);
    }

    let appInstancesCopy = ObjectUtil.deepCopy(queryResult.data.appInstances);
    let platformPermission = await this.getPlatformPermissions();
    appInstancesCopy.push(platformPermission);
    for (let appInstance of appInstancesCopy) {
      for (let module of appInstance.modules) {
        appInstance.app.permissions.push(...module.permissions);
      }
    }

    this.appInstancesCacheTime = now;
    this.writeCache(AppInstanceService.APP_INSTANCES_TIME_STORAGE_KEY, AppInstanceService.APP_INSTANCES_DATA_STORAGE_KEY, now, appInstancesCopy);
    return appInstancesCopy;
  }

  async getAppInstaceById(id: number): Promise<AppInstance[]> {
    const requestId = RequestUtil.generateRequestId();
    const now = Date.now();
    const lastFetch = this.appInstanceByIdCacheTime.get(id) ?? null;
    const cacheExpired =
      lastFetch === null ||
      now - lastFetch > this.PLATFORM_PERMISSIONS_CACHE_TTL_MS;
    if (cacheExpired) {
      this.appInstanceByIdCacheTime.set(id, now);
    }
    let queryResult = await lastValueFrom(
        this.apollo.query<any>({
          fetchPolicy: cacheExpired ? 'network-only' : 'cache-first',
          context: {
            headers: {
              'App-Id': this.appState.getAppInstanceId() ?? 0,
              'Operator-Id': this.appState.getOperatorId() ?? 0,
              'Request-Id': requestId,
              'PermissionSet-Id': this.appState.getPermissionSetId() ?? 0,
            },
          },
          query: gql`
            query ($id: Int!) {
              stationAppConsumerUserAppInstances(id: $id) {
                id
                operatorId
                operator {
                  name
                }
                modules {
                  name
                  permissions {
                    id
                    name
                    rowVer
                    permissionType
                  }
                }
                appId
                app {
                  name
                  permissions {
                    id
                    name
                    rowVer
                    permissionType
                  }
                }
              }
            }
          `,
          variables: { id },
        })
      );

    if (!queryResult.data) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error getting app instance by id', requestId);
    }

    return ObjectUtil.deepCopy(queryResult.data.stationAppConsumerUserAppInstances);
  }

  async getPlatformPermissions(): Promise<any[]> {
    const cacheExpired =
      this.platformPermissionsCacheTime === null ||
      Date.now() - this.platformPermissionsCacheTime > this.PLATFORM_PERMISSIONS_CACHE_TTL_MS;

    if (!cacheExpired) {
      const cachedData = this.readCacheData<any>(AppInstanceService.PLATFORM_PERMISSIONS_DATA_STORAGE_KEY);
      if (cachedData) {
        return cachedData;
      }
    }

    if (!this.inflightPlatformPermissions) {
      this.inflightPlatformPermissions = this.fetchPlatformPermissionsFromNetwork();
    }
    try {
      return await this.inflightPlatformPermissions;
    } finally {
      this.inflightPlatformPermissions = null;
    }
  }

  private async fetchPlatformPermissionsFromNetwork(): Promise<any[]> {
    const requestId = RequestUtil.generateRequestId();
    const now = Date.now();
    let queryResult = await lastValueFrom(
        this.apollo.query<any>({
          fetchPolicy: 'network-only',
          context: {
            headers: {
              'App-Id': this.appState.getAppInstanceId() ?? 0,
              'Operator-Id': this.appState.getOperatorId() ?? 0,
              'Request-Id': requestId,
              'PermissionSet-Id': this.appState.getPermissionSetId() ?? 0,
            },
          },
          query: gql`
            query {
              platformPermissionSet {
                id
                operator{
                  name
                }
                modules {
                  name
                  permissions {
                    id
                    name
                    permissionType
                  }
                }
                operatorId
                app{
                  name
                  permissions{
                    id
                    name
                    permissionType
                  }
                }
              }
            }
          `,
        })
      );

    if (!queryResult.data) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Error getting platform permissions', requestId);
    }

    const platformPermission = ObjectUtil.deepCopy(queryResult.data.platformPermissionSet[0]);
    this.platformPermissionsCacheTime = now;
    this.writeCache(AppInstanceService.PLATFORM_PERMISSIONS_TIME_STORAGE_KEY, AppInstanceService.PLATFORM_PERMISSIONS_DATA_STORAGE_KEY, now, platformPermission);
    return platformPermission;
  }


}
